/**
 * Add / edit a model. The provider fields and auth methods are rendered from
 * the hosting descriptors the server declares (`GET /api/ai/hostings`), and
 * the server re-validates with the same declaration on save. Save unlocks
 * only after Test passes with the values on screen (`POST /api/ai/models/test`).
 */

import { AlertTriangle, CheckCircle2, Zap } from 'lucide-react';

import type { AiModel, Capability, Hosting, ModelTestResult } from '@/api/types';
import { bannerError } from '@/api/validate';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { formatUsd } from '@/ui/format';
import { FieldLabel } from '@/ui/FieldLabel';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';
import { ActionButton, buttonState } from '@/ui/ActionButton';

import { ConfigField } from './ConfigField';
import { DISPLAY_NAME_LIMIT, MAX_OUTPUT_TOKENS, MIN_OUTPUT_TOKENS, MODEL_ID_LIMIT, PRICE_LIMIT, TYPED_FIELDS, type PriceKey } from './modelForm';
import { useModelForm, type ModelFormState } from './useModelForm';

/** The typed fields on screen show a 422 message beside them; the banner carries the rest (an embedding model hides Max output tokens). */
function placedFields(m: ModelFormState): string[] {
  return m.form.capability === 'chat' ? TYPED_FIELDS : TYPED_FIELDS.filter((f) => f !== 'max_output_tokens');
}

/** Why Save is still locked, shown under the test outcome and named by the Save button. */
const TEST_OUTCOME_ID = 'm-test-outcome';

const PROVIDER_FIXED = 'The provider is fixed once a model exists. Add a new model to move providers.';

export function ModelDialog({ hostings, model, onClose }: { hostings: Hosting[]; model?: AiModel; onClose: () => void }) {
  const m = useModelForm(hostings, model, onClose);
  return (
    <Modal
      title={model ? `Edit ${model.display_name}` : 'Add model'}
      subtitle={m.hosting.hint}
      size="wide"
      onClose={onClose}
      onSubmit={m.submit}
      footer={<DialogFooter m={m} isEdit={!!model} onClose={onClose} />}
    >
      <FormError error={bannerError(m.save.error ?? m.test.error, placedFields(m))} />
      <ModelSection m={m} hostings={hostings} model={model} />
      <ProviderSection m={m} />
      <PricingSection m={m} />
      <TestOutcome result={m.test.data} pending={m.test.isPending} stale={m.stale} />
    </Modal>
  );
}

function DialogFooter({ m, isEdit, onClose }: { m: ModelFormState; isEdit: boolean; onClose: () => void }) {
  return (
    <>
      <button
        type="button"
        className="btn btn-secondary modal-foot-start"
        disabled={m.test.isPending}
        title={m.form.capability === 'embedding' ? 'Embed one short text with these values (billed and logged)' : 'Send a one-line prompt with these values (billed and logged)'}
        onClick={(e) => m.runTest(e.currentTarget)}
      >
        <Zap aria-hidden="true" /> {m.test.isPending ? 'Testing…' : 'Test'}
      </button>
      <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <ActionButton
        state={buttonState(m.save)}
        label={isEdit ? 'Save changes' : 'Add model'}
        disabled={!m.passed}
        describedBy={m.passed ? undefined : TEST_OUTCOME_ID}
      />
    </>
  );
}

function ModelSection({ m, hostings, model }: { m: ModelFormState; hostings: Hosting[]; model?: AiModel }) {
  const { form, hosting, set } = m;
  const isEdit = !!model;
  const locked = (model?.assigned_processes.length ?? 0) > 0;
  return (
    <>
      <div className="fieldset-title">Model</div>
      <div className="grid grid-2 mb-6">
        <div className="field">
          <label htmlFor="m-name">Display name</label>
          <input id="m-name" type="text" required maxLength={DISPLAY_NAME_LIMIT} value={form.display_name} placeholder="e.g. Claude Sonnet (research)" {...fieldA11y('m-name', m.server.display_name)} onChange={(e) => set('display_name', e.target.value)} />
          <FieldError inputId="m-name" message={m.server.display_name} />
        </div>
        <div className="field">
          <FieldLabel htmlFor="m-hosting" id="m-hosting-hint" label="Provider" hint={isEdit ? PROVIDER_FIXED : undefined} />
          <select id="m-hosting" aria-describedby={isEdit ? 'm-hosting-hint' : undefined} value={form.hosting} disabled={isEdit} onChange={(e) => m.changeHosting(e.target.value)}>
            {hostings.map((h) => (
              <option key={h.id} value={h.id}>{h.label}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <FieldLabel htmlFor="m-capability" id="m-capability-hint" label="Capability" hint="Processes only list models of the capability they need." />
          <select id="m-capability" aria-describedby={locked ? 'm-capability-hint m-capability-locked' : 'm-capability-hint'} value={form.capability} disabled={locked} onChange={(e) => m.changeCapability(e.target.value as Capability)}>
            <option value="chat">Chat (answers prompts)</option>
            <option value="embedding">Embedding (turns text into vectors)</option>
          </select>
          {locked && (
            <span id="m-capability-locked" className="hint">Unassign this model from its processes to change its capability.</span>
          )}
        </div>
        <div className="field">
          <FieldLabel htmlFor="m-id" id="m-id-hint" label={hosting.model_id_label} hint={hosting.model_id_hint || undefined} />
          <input id="m-id" type="text" required {...fieldA11y('m-id', m.server.model_id, hosting.model_id_hint ? 'm-id-hint' : undefined)} className="mono-input" maxLength={MODEL_ID_LIMIT} value={form.model_id} onChange={(e) => set('model_id', e.target.value)} />
          <FieldError inputId="m-id" message={m.server.model_id} />
        </div>
      </div>
    </>
  );
}

function ProviderSection({ m }: { m: ModelFormState }) {
  const { form, auth, set } = m;
  return (
    <>
      <div className="fieldset-title">Provider settings</div>
      <div className="grid grid-2 mb-6">
        <div className="field span-2">
          <FieldLabel htmlFor="m-auth" id="m-auth-hint" label="Authentication" hint={auth?.hint || undefined} />
          <select id="m-auth" aria-describedby={auth?.hint ? 'm-auth-hint' : undefined} value={auth?.id ?? ''} onChange={(e) => set('auth', e.target.value)}>
            {m.authMethods.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </div>
        {m.fields.map((f) => (
          <ConfigField
            key={f.key}
            field={f}
            value={form.config[f.key]}
            stored={m.isStored(f.key)}
            onChange={(value) => m.setConfig(f.key, value)}
          />
        ))}
        {form.capability === 'chat' && (
          <div className="field">
            <label htmlFor="m-max">Max output tokens</label>
            <input id="m-max" type="number" min={MIN_OUTPUT_TOKENS} max={MAX_OUTPUT_TOKENS} required value={form.max_output_tokens} {...fieldA11y('m-max', m.server.max_output_tokens)} onChange={(e) => set('max_output_tokens', e.target.value === '' ? '' : Number(e.target.value))} />
            <FieldError inputId="m-max" message={m.server.max_output_tokens} />
          </div>
        )}
      </div>
    </>
  );
}

function PricingSection({ m }: { m: ModelFormState }) {
  const price = (key: PriceKey, label: string) => (
    <div className="field">
      <label htmlFor={`m-${key}`}>{label}</label>
      <input id={`m-${key}`} type="number" min="0" max={PRICE_LIMIT} step="0.0001" required value={m.form[key]} placeholder="0.00" {...fieldA11y(`m-${key}`, m.server[key])} onChange={(e) => m.set(key, e.target.value)} />
      <FieldError inputId={`m-${key}`} message={m.server[key]} />
    </div>
  );
  return (
    <>
      <div className="fieldset-title">Pricing (USD per 1M tokens)</div>
      <div className="grid grid-4">
        {price('price_input', 'Input')}
        {price('price_output', 'Output')}
        {price('price_cache_write', 'Cache write')}
        {price('price_cache_read', 'Cache read')}
      </div>
      <p className="hint mt-2">
        {m.form.capability === 'embedding'
          ? 'Billed on input tokens only; use 0 for the other rates. The model must return 1,536-dimension vectors.'
          : 'Use 0 where the provider does not charge.'}
      </p>
    </>
  );
}

function TestOutcome({ result, pending, stale }: { result?: ModelTestResult; pending: boolean; stale: boolean }) {
  return (
    <div id={TEST_OUTCOME_ID} className="model-test-result" aria-live="polite">
      {!result && !pending && <p className="hint">Test these values before saving.</p>}
      {result && stale && (
        <span className="status-warn row"><AlertTriangle aria-hidden="true" /> The values changed since the last test. Test again to save.</span>
      )}
      {result && !stale && <TestReply result={result} />}
    </div>
  );
}

function TestReply({ result }: { result: ModelTestResult }) {
  if (!result.ok) {
    return (
      <>
        <span className="status-warn row"><AlertTriangle aria-hidden="true" /> {result.error}</span>
        <p className="hint">Save unlocks once a test of these values passes.</p>
      </>
    );
  }
  return (
    <span className="status-ok row">
      <CheckCircle2 aria-hidden="true" /> “{result.text}” in {result.latency_ms} ms, {result.tokens_input} in /{' '}
      {result.tokens_output} out, {formatUsd(result.cost_usd)}
    </span>
  );
}
